'use client';

import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { GameType } from '@/lib/types';
import { GameInfo } from '@/components/game-info';
import { GamePlayer } from '@/components/game-player';
import CommentSection from '@/components/CommentSection';
import { RelatedGames } from '@/components/related-games';
import { ViewCounter } from './view-counter';
import { AAMArea } from '@/components/AAMArea'; // AAMArea 임포트 추가
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { ChatProviders } from '@/components/chat-providers';
import {
  FacebookShareButton,
  TwitterShareButton,
  FacebookIcon,
  TwitterIcon,
} from 'react-share';
import { getMembership } from '@/lib/membership/membership';
import type { Membership } from '@/lib/membership/types';
import DomainExclusiveAds from '@/components/ads/DomainExclusiveAds';

export function GamePageClient({ game, initialUser }: { game: GameType, initialUser: User | null }) {
  const [currentUser, setCurrentUser] = useState<User | null>(initialUser);
  const [chatUserId, setChatUserId] = useState<string | null>(null);
  const [currentPageUrl, setCurrentPageUrl] = useState('');
  const [membership, setMembership] = useState<Membership>(null);

  useEffect(() => {
    setCurrentUser(initialUser);
    let id: string | null = null;
    if (initialUser) {
      id = initialUser.id;
    }
    setChatUserId(id);

    const fetchMembership = async () => {
      if (initialUser) {
        const memberData = await getMembership(initialUser.id);
        setMembership(memberData);
      }
    };
    fetchMembership();
    
    setCurrentPageUrl(window.location.href);
  }, [initialUser]);

  const copyLink = () => {
    navigator.clipboard.writeText(currentPageUrl)
      .then(() => alert('게임 링크가 복사되었습니다!'))
      .catch(err => {
        console.error('링크 복사 실패:', err);
        alert('링크 복사에 실패했습니다.');
      });
  };

  if (!game) {
    return <div>게임을 찾을 수 없습니다.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <GamePlayer game={game} />
          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <FacebookShareButton url={currentPageUrl} title={game.title}>
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <TwitterShareButton url={currentPageUrl} title={game.title}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <Button onClick={copyLink} variant="outline" size="sm">
                <Copy className="h-4 w-4 mr-2" />
                링크 복사
              </Button>
            </div>
            <ViewCounter gameId={game.id.toString()} currentViews={game.views} />
          </div>
          <div className="mt-4">
            <AAMArea width={728} height={90} /> {/* ClientAdUnit 대체 */}
          </div>
        </div>
        <div className="lg:col-span-1 space-y-4">
          <GameInfo game={game} />
          {chatUserId && (
            <Tabs defaultValue="chat" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="chat">채팅</TabsTrigger>
                <TabsTrigger value="comments">댓글</TabsTrigger>
              </TabsList>
              <TabsContent value="chat">
                <ChatProviders clientId={chatUserId} roomId={game.id.toString()} />
              </TabsContent>
              <TabsContent value="comments">
                <CommentSection postId={game.id.toString()} userId={currentUser?.id || ''} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
      <div className="mt-6">
      <DomainExclusiveAds /> {/* membership 프롭 제거 */}
        <RelatedGames currentGameId={game.id.toString()} />
      </div>
    </div>
  );
}